import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { DashboardLayout } from "@/components/DashboardLayout";
import { QRPreview } from "@/components/QRPreview";
import { QR_TYPES } from "@/lib/qrTypes";
import { Button } from "@/components/ui/button";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer,
  XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { ArrowLeft, Download, Pencil, MousePointerClick, Globe, Smartphone, Monitor, Copy } from "lucide-react";
import { toast } from "sonner";

const COLORS = ["#0033CC", "#00C781", "#FFB800", "#7C3AED", "#FF3B30", "#0891B2"];

export default function QRDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qrRef = useRef(null);
  const [qr, setQr] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    Promise.all([api.get(`/qrcodes/${id}`), api.get(`/qrcodes/${id}/analytics`)])
      .then(([q, a]) => { setQr(q.data); setAnalytics(a.data); })
      .catch(() => toast.error("Failed to load analytics"));
  }, [id]);

  if (!qr || !analytics) {
    return <DashboardLayout><div className="p-8 text-muted-foreground text-sm">Loading analytics...</div></DashboardLayout>;
  }

  const T = QR_TYPES[qr.type] || QR_TYPES.url;
  const copyLink = () => { navigator.clipboard.writeText(qr.redirect_url); toast.success("Link copied"); };
  const exportCsv = async () => {
    try {
      const res = await api.get(`/qrcodes/${id}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${qr.name}-scans.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Scan data exported");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-7xl mx-auto">
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </button>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-1.5 text-sm text-primary font-medium mb-1"><T.icon className="h-4 w-4" /> {T.label}</div>
            <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight">{qr.name}</h1>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={copyLink}><Copy className="h-4 w-4 mr-2" /> Copy link</Button>
            <Button variant="outline" onClick={exportCsv} data-testid="export-csv-btn"><Download className="h-4 w-4 mr-2" /> Export CSV</Button>
            <Link to={`/edit/${qr.id}`}><Button variant="outline" data-testid="edit-from-detail"><Pencil className="h-4 w-4 mr-2" /> Edit</Button></Link>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* QR card */}
          <div className="lg:col-span-4 xl:col-span-3">
            <div className="border border-border rounded-2xl bg-card p-6 lg:sticky lg:top-8">
              <div className="rounded-xl p-4 flex items-center justify-center mb-4" style={{ background: qr.design.bgColor }}>
                <QRPreview ref={qrRef} data={qr.redirect_url} design={qr.design} size={200} />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="flex-1" onClick={() => qrRef.current?.download("png", qr.name)}><Download className="h-4 w-4 mr-1" /> PNG</Button>
                <Button variant="outline" size="sm" className="flex-1" onClick={() => qrRef.current?.download("svg", qr.name)}><Download className="h-4 w-4 mr-1" /> SVG</Button>
              </div>
              <p className="text-xs text-muted-foreground break-all mt-4">{qr.redirect_url}</p>
              {qr.next_switch && (
                <div className="mt-4 p-3 rounded-lg border border-warning/30 bg-warning/5" data-testid="next-switch">
                  <p className="text-[10px] tracking-widest uppercase font-semibold text-[#b37f00] mb-1">Scheduled switch</p>
                  <p className="text-xs text-muted-foreground">{new Date(qr.next_switch.activate_at).toLocaleString()}</p>
                  <p className="text-xs truncate mt-0.5">→ {qr.next_switch.url}</p>
                </div>
              )}
            </div>
          </div>

          {/* Analytics */}
          <div className="lg:col-span-8 xl:col-span-9 space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Stat icon={MousePointerClick} label="Total scans" value={analytics.total_scans} />
              <Stat icon={Globe} label="Countries" value={analytics.by_country.length} />
              <Stat icon={Smartphone} label="Top device" value={analytics.by_device[0]?.name || "—"} />
              <Stat icon={Monitor} label="Top browser" value={analytics.by_browser[0]?.name || "—"} />
            </div>

            {/* Timeline */}
            <Panel title="Scans over time (30 days)">
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={analytics.timeline}>
                  <defs>
                    <linearGradient id="scanGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} interval={4} stroke="hsl(var(--muted-foreground))" />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid hsl(var(--border))" }} />
                  <Area type="monotone" dataKey="scans" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#scanGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </Panel>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Panel title="By country">
                {analytics.by_country.length ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={analytics.by_country.slice(0, 6)} layout="vertical">
                      <XAxis type="number" hide allowDecimals={false} />
                      <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                      <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                      <Bar dataKey="value" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <Empty />}
              </Panel>
              <Panel title="By device">
                {analytics.by_device.length ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={analytics.by_device} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(e) => e.name}>
                        {analytics.by_device.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <Empty />}
              </Panel>
            </div>

            {/* Recent scans */}
            <Panel title="Recent scans">
              {analytics.recent.length ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                        <th className="py-2 pr-4 font-semibold">Time</th>
                        <th className="py-2 pr-4 font-semibold">Location</th>
                        <th className="py-2 pr-4 font-semibold">Device</th>
                        <th className="py-2 pr-4 font-semibold">Browser</th>
                        <th className="py-2 font-semibold">OS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.recent.map((s) => (
                        <tr key={s.id} className="border-b border-border/60">
                          <td className="py-2.5 pr-4 text-muted-foreground whitespace-nowrap">{new Date(s.timestamp).toLocaleString()}</td>
                          <td className="py-2.5 pr-4">{s.city !== "Unknown" ? `${s.city}, ` : ""}{s.country}</td>
                          <td className="py-2.5 pr-4">{s.device_type}</td>
                          <td className="py-2.5 pr-4">{s.browser}</td>
                          <td className="py-2.5">{s.os}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <Empty text="No scans yet. Share your QR code to start collecting data." />}
            </Panel>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="border border-border rounded-xl bg-card p-4">
      <div className="flex items-center gap-1.5 mb-1"><Icon className="h-3.5 w-3.5 text-primary" /><p className="text-xs text-muted-foreground truncate">{label}</p></div>
      <p className="font-display text-2xl font-bold truncate">{value}</p>
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <div className="border border-border rounded-xl bg-card p-5">
      <h3 className="font-display font-semibold mb-4">{title}</h3>
      {children}
    </div>
  );
}

function Empty({ text = "No data yet." }) {
  return <p className="text-sm text-muted-foreground py-8 text-center">{text}</p>;
}
